import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ManageUsersActionType,
  useManageUsersContext,
} from "../../../../context/ManageUsersContext";
import useAlert from "../../../../hooks/useAlert";
import {
  IUser,
  PatientDetailsFormInputs,
} from "../../../../models/users.interface";
import usersService from "../../../../services/users.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import EditUser from "../../EditUser/EditUser";
import SecurityDetails from "../../EditUser/SecurityDetails/SecurityDetails";
import PatientGeneralDetails from "../PatientGeneralDetails/PatientGeneralDetails";
import { Permissions } from "../../../../models/permission";

const PatientDetailsPortal = () => {
  const [domReady, setDomReady] = useState(false);

  useEffect(() => {
    setDomReady(true);
  }, []);

  const {
    state: { loading, selectedUser },
    actions: { loadUsers },
    dispatch,
  } = useManageUsersContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const userUpdatedHandler = async () => {
    await loadUsers({ isPatients: true });
  };

  const cancelUserSelectionHandler = () => {
    dispatch({
      type: ManageUsersActionType.SET_SELECTED_USER,
      payload: null,
    });
  };

  const toggleUserActive = async (isActive: boolean) => {
    if (!selectedUser) {
      return;
    }
    const { success, errorCode } = await usersService.updateUserState(
      selectedUser.id,
      isActive
    );

    if (!success) {
      const { message, support } = getAlertMessageAndSupportContent(
        isActive ? "ERROR.ACTIVATE_USER" : "ERROR.DEACTIVATE_USER",
        `An error occurred while trying to ${
          isActive ? "activate" : "deactivate"
        } the user.<br></br>Please try again.`,
        {
          br: () => <br></br>,
        },
        errorCode
      );
      showErrorAlert(message, support);
      return;
    }

    await loadUsers({ isPatients: true });
  };

  let formData;
  if (selectedUser) formData = mapToPatientDetailsForm(selectedUser);

  const container = document.getElementById("userInfoWrapper");

  return domReady && container
    ? createPortal(
        <>
          <EditUser
            isUserSelected={!loading && !!selectedUser}
            detailsTabContent={
              <PatientGeneralDetails
                userId={selectedUser?.id ?? ""}
                userStatus={selectedUser?.status}
                createdAt={selectedUser?.createdAt}
                activatedAt={selectedUser?.activatedAt}
                totalEcgs={selectedUser?.totalEcgs}
                deviceSn={selectedUser?.deviceSn}
                lastEcgDate={selectedUser?.lastEcgDate}
                formData={formData}
                onCancel={cancelUserSelectionHandler}
                onToggleUserActive={toggleUserActive}
                onUpdated={userUpdatedHandler}
              />
            }
            securityTabContent={
              <SecurityDetails
                userId={selectedUser?.id ?? ""}
                userEmail={selectedUser?.email ?? "N/A"}
                userStatus={selectedUser?.status ?? "disabled"}
                userType={"patient"}
                onUpdated={userUpdatedHandler}
              />
            }
          />
          <AlertModal {...alertProps}>{alertMessage}</AlertModal>
        </>,
        container
      )
    : null;
};

export default PatientDetailsPortal;

const mapToPatientDetailsForm = (user: IUser): PatientDetailsFormInputs => {
  const {
    email,
    firstName,
    lastName,
    phone,
    organization,
    prescribingPhysician,
    beltSize,
    //sendReportByEmail,
    dateOfBirth,
    gender,
    //emailVerification,
    reference,
    hasPacemaker,
  } = user;

  const patientFormInputs: PatientDetailsFormInputs = {
    entity: organization?.id ?? "",
    firstName,
    lastName,
    email: email ?? "",
    phone,
    prescribingPhysician: prescribingPhysician?.id ?? "",
    beltSize: beltSize,
    dateOfBirth,
    gender,
    //emailVerificationRequired: emailVerification ?? false,
    //sendReportByEmail: sendReportByEmail ?? false,
    reference,
    hasPacemaker: hasPacemaker
      ? "true"
      : hasPacemaker === false
      ? "false"
      : undefined,
    canViewEcgs: user.permissions.includes(Permissions.CanViewEcgs),
    canSendEcgsViaMail: user.permissions.includes(
      Permissions.CanSendEcgsViaMail
    ),
    showEcgPreview: user.permissions.includes(Permissions.ShowEcgPreview),
  };

  return patientFormInputs;
};
